import _ from 'lodash';

import Entity from './Entity';

import Fee from './Fee';
import BillingProfile from './BillingProfile';
import PromoCode from './PromoCode';
import AccessDevice from './AccessDevice';
import SalesLimit from './SalesLimit';

import { dayOfTheMonthFieldOptions } from './util';

import { FacilitySettings } from './Facility';
import {
  queryGetFacilitySettings
} from '../Queries';


const facilitySettingsQueryFields = [
  'display',
  'require_car_info',
  'first_month_proration',
  'first_month_proration_day',
  'invoice_notes',
  'access_device_message',
  'lost_device_request',
  { 'fees': [
    'id',
    'type',
    'price',
    'tax_1',
    'tax_2',
    'tax_3',
    'tax_4',
    'gross_price',
    'start_date'
  ] },
  { 'access_devices': [
    'id',
    'type',
    'required',
    'offered',
    { 'fee': [
        'id',
        'type',
        'price',
        'tax_1',
        'tax_2',
        'tax_3',
        'tax_4',
        'gross_price',
        'start_date'
    ] }
  ] },
  { 'survey_questions': [
    'id',
    'type',
    'structure',
    'order'
  ] }
];


const rateEntityFieldsWithDynamicAllowedValues = {
  display: { enumName: 'DisplayEnum' },
  pass_type: { enumName: 'PassTypeEnum' },
  access_hours: { enumName: 'AccessHoursEnum' },
  require_car_info: { enumName: 'RequireCarInfoEnum' },
  first_month_proration: { enumName: 'FirstMonthProrationEnum' }
};

const rateEntityFieldsWithStaticAllowedValues = {
  active: {
    options: [
      { value: true, label: 'Active' },
      { value: false, label: 'Inactive' }
    ]
  },
  first_month_proration_day: {
    options: dayOfTheMonthFieldOptions
  },
  tax_exempt: {
    options: [
      { value: true, label: 'Yes' },
      { value: false, label: 'No' }
    ]
  },
  lost_device_request: {
    options: [
      { value: true, label: 'Allow parkers to request a lost device.' },
      { value: false, label: 'Do not allow parkers to request a lost device.' }
    ]
  },
  months_to_sell_in_advance: {
    options: [
      { value: 1, label: '1' },
      { value: 2, label: '2' },
      { value: 3, label: '3' },
      { value: 4, label: '4' },
      { value: 5, label: '5' },
      { value: 6, label: '6' },
      { value: 7, label: '7' },
      { value: 8, label: '8' },
      { value: 9, label: '9' },
      { value: 10, label: '10' },
      { value: 11, label: '11' },
      { value: 12, label: '12' }
    ]
  }
};


export default class Rate {
  fields = {
    id: {
      value: null
    },
    billing_profile_id: {
      value: null
    },
    name: {
      value: null
    },
    description: {
      value: null
    },
    active: {
      value: null
    },
    display: {
      value: null
    },
    pass_type: {
      value: null
    },
    access_hours: {
      value: null
    },
    location: {
      value: null
    },
    fees: {
      monthly: {
        value: []
      },
      activation: {
        value: []
      },
      lost_device: {
        value: []
      },
      credit_card: {
        value: []
      },
      late: {
        value: []
      }
    },
    require_car_info: {
      value: null
    },
    first_month_proration: {
      value: null
    },
    first_month_proration_day: {
      value: null
    },
    tax_exempt: {
      value: null
    },
    access_device_message: {
      value: null
    },
    lost_device_request: {
      value: null
    },
    months_to_sell_in_advance: {
      value: null
    },
    accessSystemMeta: {
      value: {}
    },
    created_at: {
      value: null
    },
    updated_at: {
      value: null
    },
    billing_profile: {
      value: null
    },
    promo_codes: {
      value: []
    },
    access_devices: {
      value: []
    },
    sales_limits: {
      value: []
    },
    display_price: {
      value: null
    }
  };

  validations = {
    name: {
      message: 'Name is required'
    },
    pass_type: {
      message: 'Pass Type is required'
    },
    access_hours: {
      message: 'Access Hours is required'
    },
    require_car_info: {
      message: 'Specify if car information is required on purchase'
    },
    billing_profile_id: {
      message: 'A Rate must have a Billing Profile'
    },
    months_to_sell_in_advance: {
      message: 'Specify the number of months to sell in advance'
    }
  }

  get allFees() {
    const fees = Object.keys(this.fields.fees).reduce((fees, feeType) => {
      const feesForType = this.fields.fees[feeType].value.reduce((feesForType, fee) => [ ...feesForType, fee ], []);

      return [ ...fees, ...feesForType ];
    }, []);

    const accessDeviceFees = this.fields.access_devices.value.reduce((accessDeviceFees, accessDevice) => {
      const accessDeviceFee = accessDevice.fields.fee.value;
      if (accessDeviceFee) {
        accessDeviceFees.push(accessDeviceFee);
      }
      return accessDeviceFees;
    }, []);

    return [ ...fees, ...accessDeviceFees ];
  }


  static createFromQueryValues(values) {
    const rate = new this();

    for (let field in rate.fields) {
      if (values.hasOwnProperty(field)) {
        switch (field) {
          case 'created_at':
          case 'updated_at':
            rate.fields[field].value = (values[field] === null) ?
              values[field] :
              new Date(Number(values[field]));
            break;
          case 'billing_profile':
            rate.fields[field].value = (values[field] === null) ?
              values[field] :
              BillingProfile.createFromQueryValues(values[field]);
            break;
          case 'promo_codes':
            if (Array.isArray(values[field])) {
              for (let promoCode of values[field]) {
                rate.fields[field].value.push(PromoCode.createFromQueryValues(promoCode));
              }
            }
            break;
          case 'access_devices':
            if (Array.isArray(values[field])) {
              for (let accessDevice of values[field]) {
                rate.fields[field].value.push(AccessDevice.createFromQueryValues(accessDevice));
              }
            }
            break;
          case 'sales_limits':
            if (Array.isArray(values[field])) {
              for (let salesLimit of values[field]) {
                rate.fields[field].value.push(SalesLimit.createFromQueryValues(salesLimit));
              }
            }
            break;
          case 'fees':
            if (Array.isArray(values[field])) {
              for (let type in rate.fields[field]) {
                const feesOfType = values[field].filter(e => e.type === type.toUpperCase());
                const fees = [];
                for (let fee of feesOfType) {
                  fees.push(Fee.createFromQueryValues(fee));
                }
                // Sort by start date, ascending
                fees.sort((feeA, feeB) => {
                  const startDateA = feeA.fields.start_date.value;
                  const startDateB = feeB.fields.start_date.value;
                  return (startDateA <= startDateB) ? (startDateA < startDateB) ? -1 : 0 : 1;
                });
                rate.fields[field][type].value = fees;
              }
            }
            break;
          default:
            rate.fields[field].value = values[field];
        }
      }
    }

    return rate;
  }


  static createFromDefaultValuesForFacility(facilityId) {
    return new Promise((resolve, reject) => {
      queryGetFacilitySettings({
        facility_id: facilityId
      }, facilitySettingsQueryFields).then(response => {
        if (response.errors) {
          reject(response.errors.map(error => error.message));
          return;
        }

        const rate = new this();

        if (response.data.getFacilitySettings) {
          const facilitySettings = FacilitySettings.createFromQueryValues(response.data.getFacilitySettings);

          for (let field in facilitySettings.fields) {
            if (
              facilitySettings.fields[field].value !== null &&
              rate.fields.hasOwnProperty(field)
            ) {
              switch (field) {
                case 'fees':
                  for (let feeType in facilitySettings.fields[field]) {
                    if (!Object.keys(rate.fields[field]).includes(feeType)) continue;
  
                    rate.fields[field][feeType].value = facilitySettings.fields[field][feeType].value;
                  }
                  break;
                default:
                  rate.fields[field].value = facilitySettings.fields[field].value;
              }
            }
          }
        }

        resolve(rate);
      }, error => {
        reject([error]);
      });
    });
  }


  static getFieldAllowedValues(fields) {
    return Entity.getFieldAllowedValues(
      rateEntityFieldsWithStaticAllowedValues,
      rateEntityFieldsWithDynamicAllowedValues,
      fields
    );
  }


  getQueryFieldValues() {
    const values = {
      rate: _.cloneDeep(RateInput),
      accessLevelCode: this.fields.accessSystemMeta.value.accessLevelCode ?? null,
      fees: [],
      promo_codes: [],
      access_devices: [],
      sales_limits: []
    };

    for (let field in values.rate) {
      switch (field) {
        case 'id':
          if (this.fields[field].value === null) {
            delete values.rate.id;
          }
          else {
            values.rate[field] = this.fields[field].value;
          }
          break;
        case 'accessLevelCode':
          values.rate.accessLevelCode = this.fields.accessSystemMeta.value.accessLevelCode ?? null;
          break;
        default:
          values.rate[field] = this.fields[field].value;
      }
    }

    // Fees
    for (let elementType in this.fields.fees) {
      if (this.fields.fees[elementType].value.length) {
        for (let fee of this.fields.fees[elementType].value) {
          const feeQueryFieldValues = fee.getQueryFieldValues();
          if (feeQueryFieldValues) {
            values.fees.push(feeQueryFieldValues);
          }
        }
      }
    }

    // Promo Codes
    if (this.fields.promo_codes.value.length) {
      for (let promoCode of this.fields.promo_codes.value) {
        const promoCodeQueryFieldValues = promoCode.getQueryFieldValues();
        if (promoCodeQueryFieldValues) {
          values.promo_codes.push(promoCodeQueryFieldValues);
        }
      }
    }

    // Access Devices
    if (this.fields.access_devices.value.length) {
      for (let accessDevice of this.fields.access_devices.value) {
        const accessDeviceQueryFieldValues = accessDevice.getQueryFieldValues();
        if (accessDeviceQueryFieldValues) {
          values.access_devices.push(accessDeviceQueryFieldValues);
        }
      }
    }

    // Sales Limits
    if (this.fields.sales_limits.value.length) {
      for (let salesLimit of this.fields.sales_limits.value) {
        const salesLimitQueryFieldValues = salesLimit.getQueryFieldValues();
        if (salesLimitQueryFieldValues) {
          values.sales_limits.push(salesLimitQueryFieldValues);
        }
      }
    }

    return values;
  }


  isEditable() {
    /*
     * Below is the code moved from the Rate Detail Page. This should be
     * something the Rate entity calculates directly. However, the logic
     * below is overly drastic and does not allow for even simple edits
     * and should be verified with Parking Advisors team.
     */
    // const startDate = new Date(monthlyFees[0].fields.start_date.value);
    // const startMonth = (startDate.getUTCMonth() + 1).toString().padStart(2, '0');
    // const startDay = startDate.getUTCDate().toString().padStart(2, '0');
    // const rateStartDate = `${startDate.getUTCFullYear()}-${startMonth}-${startDay}`;

    // const date = new Date();
    // const currentMonth = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    // const currentDay = date.getUTCDate().toString().padStart(2, '0');
    // const currentDate = `${date.getUTCFullYear()}-${currentMonth}-${currentDay}`;

    // return (currentDate < rateStartDate);

    return true;
  }


  validate(newMode) {
    if (newMode) {
      for (let feeType in this.fields.fees) {
        for (let fee of this.fields.fees[feeType].value) {
          const validate = fee.validate();
          if (!validate.valid) {
            return validate;
          }
        }
      }

      for (let device of this.fields.access_devices.value) {
        if (device.fields.fee.value) {
          const validate = device.fields.fee.value.validate();
          if (!validate.valid) {
            return validate;
          }
        }
      }
    }

    for (const field in this.validations) {
      if (!this.fields[field].value) {
        return { valid: false, error: this.validations[field].message };
      }
    }

    return { valid: true }
  }
}


export const RateInput = {
  id: null,
  billing_profile_id: null,
  name: null,
  description: null,
  active: null,
  display: null,
  pass_type: null,
  access_hours: null,
  location: null,
  require_car_info: null,
  first_month_proration: null,
  first_month_proration_day: null,
  tax_exempt: null,
  access_device_message: null,
  lost_device_request: null,
  months_to_sell_in_advance: null
}


export class RateMeta {
  fields = {
    id: {
      value: null
    },
    name: {
      value: null
    }
  };


  static createFromQueryValues(values) {
    let rateMeta = new this();

    for (let field in rateMeta.fields) {
      if (values.hasOwnProperty(field)) {
        switch (field) {
          default:
            rateMeta.fields[field].value = values[field];
        }
      }
    }

    return rateMeta;
  }
}
